import React, { useEffect, useState } from 'react';
import { adminAPI } from '../lib/api';
import { toast } from 'sonner';
import { Building2, Users, FolderKanban, Leaf, Activity } from 'lucide-react';

const StatCard = ({ label, value, sub, icon: Icon, accent = false, testId }) => (
  <div className="bento-card p-6" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <p className="eyebrow">{label}</p>
        <p
          className="mt-3 metric"
          style={{
            fontSize: 32,
            fontWeight: 600,
            color: accent ? 'var(--c-terracotta)' : 'var(--c-pine)',
            lineHeight: 1.1,
          }}
        >
          {value}
        </p>
        {sub && (
          <p className="mt-1 text-xs" style={{ color: 'var(--c-ink-3)' }}>{sub}</p>
        )}
      </div>
      <div
        className="w-10 h-10 flex items-center justify-center"
        style={{ background: accent ? '#F8E0D9' : 'var(--c-pine-soft)', borderRadius: 4 }}
      >
        <Icon
          className="w-5 h-5"
          style={{ color: accent ? 'var(--c-terracotta)' : 'var(--c-pine)' }}
          strokeWidth={1.75}
        />
      </div>
    </div>
  </div>
);

const AdminOverview = () => {
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, a] = await Promise.all([adminAPI.getStats(), adminAPI.getActivity(8)]);
        setStats(s.data);
        setEvents(a.data.events);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load overview');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading)
    return <div className="p-8 text-sm" style={{ color: 'var(--c-ink-2)' }}>Loading overview…</div>;

  return (
    <div className="p-8" data-testid="admin-overview">
      <div className="page-header -mx-8 -mt-8 mb-8 px-8 py-6">
        <p className="eyebrow">Portal administration</p>
        <h1
          className="mt-2 text-2xl font-semibold tracking-tight"
          style={{ fontFamily: 'Chivo, sans-serif', color: 'var(--c-ink)' }}
        >
          Overview
        </h1>
        <p className="mt-1 text-sm" style={{ color: 'var(--c-ink-2)' }}>
          Platform usage at a glance.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <StatCard label="Companies" value={stats.total_companies} sub={`${stats.active_companies} active`} icon={Building2} testId="stat-companies" />
        <StatCard label="Architect users" value={stats.total_users} sub={stats.suspended_users ? `${stats.suspended_users} suspended` : 'All active'} icon={Users} testId="stat-users" />
        <StatCard label="Projects" value={stats.total_projects} sub="Across the platform" icon={FolderKanban} testId="stat-projects" />
        <StatCard label="Embodied carbon" value={`${stats.total_carbon_tonnes.toLocaleString()} t`} sub="Total CO₂e calculated" icon={Leaf} accent testId="stat-carbon" />
      </div>

      <div className="bento-card p-0 overflow-hidden" data-testid="activity-snippet">
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--c-line)' }}>
          <p className="eyebrow">Recent activity</p>
          <Activity className="w-4 h-4" style={{ color: 'var(--c-ink-3)' }} strokeWidth={1.75} />
        </div>
        {events.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm" style={{ color: 'var(--c-ink-3)' }}>No activity yet.</div>
        ) : (
          <ul>
            {events.map((e, idx) => (
              <li
                key={idx}
                className="px-6 py-3 flex items-start gap-3"
                style={{ borderTop: idx > 0 ? '1px solid var(--c-line)' : 'none' }}
              >
                <span
                  className="status-dot mt-1.5"
                  style={{ background: e.type === 'user_signup' ? 'var(--c-pine)' : 'var(--c-terracotta)' }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm" style={{ color: 'var(--c-ink)' }}>{e.label}</p>
                  <p className="text-[11px] mt-0.5" style={{ color: 'var(--c-ink-3)' }}>
                    {e.actor} · {new Date(e.created_at).toLocaleDateString()} {new Date(e.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminOverview;
